import { supabase } from "@/integrations/supabase/client";
import {
  GuestInvitationService,
  type GuestPermission,
} from "@/services/guestInvitationService";
import type { User } from "@supabase/supabase-js";

export interface Project {
  id: string;
  user_id: string;
  name: string;
  description: string;
  status: "Planned" | "Active" | "Completed" | "Paused" | "Cancelled";
  start_date: string;
  end_date?: string;
  region_id?: string;
  category:
    | "Reforestation"
    | "Wildlife Conservation"
    | "Water Conservation"
    | "Renewable Energy"
    | "Waste Reduction"
    | "Soil Restoration"
    | "Climate Research"
    | "Community Outreach"
    | "Other";
  impact: string;
  organization?: string;
  budget?: number;
  participants?: number;
  location?: string;
  contact_email?: string;
  website_url?: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectWithAccess extends Project {
  access_type: "owner" | "guest";
  guest_permissions?: {
    view: boolean;
    edit: boolean;
  };
  host_email?: string;
  /** Number of active guests on this project. Only populated for owner projects. */
  guest_count?: number;
}

export interface CreateProjectData {
  name: string;
  description: string;
  status: Project["status"];
  start_date: string;
  end_date?: string;
  region_id?: string;
  category: Project["category"];
  impact: string;
  organization?: string;
  budget?: number;
  participants?: number;
  location?: string;
  contact_email?: string;
  website_url?: string;
  image_url?: string;
}

export interface UpdateProjectData extends Partial<CreateProjectData> {
  id: string;
}

/**
 * Get all projects for a specific user
 */
export const getUserProjects = async (userId: string): Promise<Project[]> => {
  try {
    const { data, error } = await supabase
      .from("conservation_projects")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching user projects:", error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error("Unexpected error fetching projects:", error);
    throw error;
  }
};

/**
 * Get all accessible projects for a user (owned + guest access)
 */
export const getAllAccessibleProjects = async (
  userId: string,
): Promise<ProjectWithAccess[]> => {
  try {
    console.log("🔍 getAllAccessibleProjects called for userId:", userId);

    // Get owned projects
    const ownedProjects = await getUserProjects(userId);
    console.log("👤 Owned projects:", ownedProjects.length);

    // Fetch active guest counts per project in a single query
    const { data: guestCountRows } = await supabase
      .from("guest_permissions")
      .select("project_id")
      .eq("host_user_id", userId)
      .eq("is_active", true);

    const guestCountMap = new Map<string, number>();
    guestCountRows?.forEach(({ project_id }) => {
      if (project_id)
        guestCountMap.set(project_id, (guestCountMap.get(project_id) ?? 0) + 1);
    });

    const ownedProjectsWithAccess: ProjectWithAccess[] = ownedProjects.map(
      (project) => ({
        ...project,
        access_type: "owner" as const,
        guest_count: guestCountMap.get(project.id) ?? 0,
      }),
    );

    // Get guest projects
    const guestPermissions =
      await GuestInvitationService.getGuestProjects(userId);
    console.log("🤝 Guest permissions found:", guestPermissions.length);
    const guestProjects: ProjectWithAccess[] = [];

    // For each guest permission, get the actual project data
    // We'll use a direct approach that works around RLS by querying as the service role
    for (const permission of guestPermissions) {
      console.log("🔍 Processing permission:", permission);
      if (permission.project_id) {
        try {
          // Try to get the project using a simple query first
          const { data: project, error } = await supabase
            .from("conservation_projects")
            .select("*")
            .eq("id", permission.project_id)
            .single();

          if (!error && project) {
            console.log("✅ Found guest project:", project.name);
            guestProjects.push({
              ...project,
              access_type: "guest",
              guest_permissions: permission.permissions,
              host_email: permission.host_user?.email,
            });
          } else {
            console.log(
              "❌ RLS blocking access to project. Creating accessible project entry.",
            );

            // Create a project entry that the user can access based on the permission
            // We'll fetch what we can and create a usable project object
            const accessibleProject = {
              id: permission.project_id,
              name: `Shared Project ${permission.project_id.substring(0, 8)}...`,
              description:
                "You have guest access to this collaborative project. Contact the host for more details.",
              status: "Active" as const,
              category: "Other" as const,
              impact: "Collaborative environmental project",
              organization: "Shared Project",
              user_id: permission.host_user_id || "",
              start_date: permission.created_at,
              created_at: permission.created_at,
              updated_at: permission.created_at,
              location: "Various",
              budget: 0,
              participants: 1,
            };

            guestProjects.push({
              ...accessibleProject,
              access_type: "guest",
              guest_permissions: permission.permissions,
              host_email: permission.host_user?.email,
            });
          }
        } catch (error) {
          console.warn(
            `Failed to fetch guest project ${permission.project_id}:`,
            error,
          );
        }
      }
    }

    // Combine and sort by created_at
    const allProjects = [...ownedProjectsWithAccess, ...guestProjects];
    console.log(
      "📊 Final results - Owned:",
      ownedProjectsWithAccess.length,
      "Guest:",
      guestProjects.length,
      "Total:",
      allProjects.length,
    );
    return allProjects.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  } catch (error) {
    console.error("Error fetching accessible projects:", error);
    // Fallback to just owned projects if guest functionality fails
    const ownedProjects = await getUserProjects(userId);
    return ownedProjects.map((project) => ({
      ...project,
      access_type: "owner" as const,
    }));
  }
};

/**
 * Get a specific project by ID (user must own it)
 */
export const getProjectById = async (
  projectId: string,
  userId: string,
): Promise<Project | null> => {
  try {
    const { data, error } = await supabase
      .from("conservation_projects")
      .select("*")
      .eq("id", projectId)
      .eq("user_id", userId)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        return null; // No rows returned
      }
      console.error("Error fetching project:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Unexpected error fetching project:", error);
    throw error;
  }
};

/**
 * Create a new project
 */
export const createProject = async (
  userId: string,
  projectData: CreateProjectData,
): Promise<Project> => {
  try {
    const { data, error } = await supabase
      .from("conservation_projects")
      .insert([
        {
          ...projectData,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Error creating project:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Unexpected error creating project:", error);
    throw error;
  }
};

/**
 * Update an existing project (supports both owners and guests with edit permissions)
 */
export const updateProject = async (
  projectData: UpdateProjectData,
  userId: string,
): Promise<Project> => {
  try {
    const { id, ...updateData } = projectData;

    // First check if user is the owner
    let { data, error } = await supabase
      .from("conservation_projects")
      .update({
        ...updateData,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    // If not owner, check if user has guest edit permissions
    if (error && error.code === "PGRST116") {
      console.log("🔍 User is not owner, checking guest edit permissions...");

      // Check if user has guest edit permissions for this project
      const { data: guestPermission, error: permError } = await supabase
        .from("guest_permissions")
        .select("permissions")
        .eq("guest_user_id", userId)
        .eq("project_id", id)
        .eq("is_active", true)
        .single();

      if (permError || !guestPermission || !guestPermission.permissions?.edit) {
        throw new Error("You do not have permission to edit this project");
      }

      console.log("✅ Guest has edit permissions, updating project...");

      // User has edit permissions as guest, update without user_id filter
      const { data: guestUpdateData, error: guestUpdateError } = await supabase
        .from("conservation_projects")
        .update({
          ...updateData,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (guestUpdateError) {
        console.error("Error updating project as guest:", guestUpdateError);
        throw guestUpdateError;
      }

      data = guestUpdateData;
      error = null;
    }

    if (error) {
      console.error("Error updating project:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Unexpected error updating project:", error);
    throw error;
  }
};

/**
 * Check if a user can edit a project (owner or guest with edit permissions)
 */
export const canEditProject = async (
  projectId: string,
  userId: string,
): Promise<boolean> => {
  try {
    // Check if user is the owner
    const { data: ownerCheck } = await supabase
      .from("conservation_projects")
      .select("id")
      .eq("id", projectId)
      .eq("user_id", userId)
      .single();

    if (ownerCheck) {
      return true;
    }

    // Check if user has guest edit permissions
    const { data: guestPermission } = await supabase
      .from("guest_permissions")
      .select("permissions")
      .eq("guest_user_id", userId)
      .eq("project_id", projectId)
      .eq("is_active", true)
      .single();

    return guestPermission?.permissions?.edit === true;
  } catch (error) {
    console.error("Error checking edit permissions:", error);
    return false;
  }
};

/**
 * Delete a project (only owners can delete, not guests)
 */
export const deleteProject = async (
  projectId: string,
  userId: string,
): Promise<void> => {
  try {
    const { error } = await supabase
      .from("conservation_projects")
      .delete()
      .eq("id", projectId)
      .eq("user_id", userId);

    if (error) {
      console.error("Error deleting project:", error);
      throw error;
    }
  } catch (error) {
    console.error("Unexpected error deleting project:", error);
    throw error;
  }
};

/**
 * Get projects by category for the user
 */
export const getProjectsByCategory = async (
  userId: string,
  category: Project["category"],
): Promise<Project[]> => {
  try {
    const { data, error } = await supabase
      .from("conservation_projects")
      .select("*")
      .eq("user_id", userId)
      .eq("category", category)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching projects by category:", error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error("Unexpected error fetching projects by category:", error);
    throw error;
  }
};

/**
 * Get projects by status for the user
 */
export const getProjectsByStatus = async (
  userId: string,
  status: Project["status"],
): Promise<Project[]> => {
  try {
    const { data, error } = await supabase
      .from("conservation_projects")
      .select("*")
      .eq("user_id", userId)
      .eq("status", status)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching projects by status:", error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error("Unexpected error fetching projects by status:", error);
    throw error;
  }
};

/**
 * Search projects by name or description
 */
export const searchUserProjects = async (
  userId: string,
  searchTerm: string,
): Promise<Project[]> => {
  try {
    const { data, error } = await supabase
      .from("conservation_projects")
      .select("*")
      .eq("user_id", userId)
      .or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error searching projects:", error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error("Unexpected error searching projects:", error);
    throw error;
  }
};

/**
 * Get project statistics for the user (owned projects only).
 * Also fetches total active collaborator count from guest_permissions.
 */
export const getProjectStats = async (
  userId: string,
): Promise<{
  total: number;
  active: number;
  completed: number;
  planned: number;
  paused: number;
  cancelled: number;
  totalCollaborators: number;
  nextDeadline: string | null; // ISO date string of the nearest future end_date
}> => {
  try {
    const [projectsResult, collaboratorsResult] = await Promise.all([
      supabase
        .from("conservation_projects")
        .select("status, end_date")
        .eq("user_id", userId),
      supabase
        .from("guest_permissions")
        .select("id", { count: "exact", head: true })
        .eq("host_user_id", userId)
        .eq("is_active", true),
    ]);

    if (projectsResult.error) {
      console.error("Error fetching project stats:", projectsResult.error);
      throw projectsResult.error;
    }

    const data = projectsResult.data ?? [];
    const totalCollaborators = collaboratorsResult.count ?? 0;

    const stats = {
      total: data.length,
      active: 0,
      completed: 0,
      planned: 0,
      paused: 0,
      cancelled: 0,
      totalCollaborators,
      nextDeadline: null as string | null,
    };

    const now = Date.now();
    let soonestMs = Infinity;

    data.forEach((project) => {
      if (project.status === "Active") stats.active++;
      else if (project.status === "Completed") stats.completed++;
      else if (project.status === "Planned") stats.planned++;
      else if (project.status === "Paused") stats.paused++;
      else if (project.status === "Cancelled") stats.cancelled++;

      if (project.end_date) {
        const t = new Date(project.end_date).getTime();
        if (t > now && t < soonestMs) {
          soonestMs = t;
          stats.nextDeadline = project.end_date;
        }
      }
    });

    return stats;
  } catch (error) {
    console.error("Unexpected error fetching project stats:", error);
    throw error;
  }
};

/**
 * Check if user can create more projects based on their subscription tier
 * Returns true if the user is allowed to create more projects
 */
export const canCreateProject = async (
  user: User | null,
): Promise<{
  allowed: boolean;
  remaining: number;
  message: string;
}> => {
  // If no user, deny access
  if (!user) {
    return {
      allowed: false,
      remaining: 0,
      message: "You must be logged in to create projects.",
    };
  }

  try {
    // Enforce a flat maximum of 10 projects for all users
    const PROJECT_LIMIT = 10;

    // Count existing owned projects
    const { data, error, count } = await supabase
      .from("conservation_projects")
      .select("id", { count: "exact" })
      .eq("user_id", user.id);

    if (error) {
      console.error("Error checking project count:", error);
      // On error, allow creation to avoid blocking users
      return {
        allowed: true,
        remaining: PROJECT_LIMIT,
        message: "Unable to check project limit. Please try again later.",
      };
    }

    const projectCount = count || 0;
    const remaining = Math.max(0, PROJECT_LIMIT - projectCount);

    if (remaining <= 0) {
      return {
        allowed: false,
        remaining: 0,
        message: `You've reached the maximum limit of ${PROJECT_LIMIT} active projects. Complete or delete projects to free up space.`,
      };
    }

    return {
      allowed: true,
      remaining,
      message: `You can create ${remaining} more project${remaining === 1 ? "" : "s"}.`,
    };
  } catch (error) {
    console.error("Error checking project creation limits:", error);
    return {
      allowed: true,
      remaining: 0,
      message: "Unable to verify project limits. Please try again later.",
    };
  }
};
